import { motion, AnimatePresence } from 'framer-motion';
import { Search, X } from 'lucide-react';
import { AdminData } from '../AdminLoader';
import Pagination from '@repo/ui/components/Pagination';
import { useIsMobile } from '../../../hooks/useIsMobile';

export const LoadingState = () => (
  <motion.div
    className="flex h-full items-center justify-center"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.2 }}
  >
    <div className="text-[var(--anl-text-muted)]">Loading…</div>
  </motion.div>
);

interface EmptyStateProps {
  message?: string;
}

export const EmptyState = ({ message = 'No users found' }: EmptyStateProps) => (
  <motion.div
    className="flex h-full items-center justify-center"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.2 }}
  >
    <div className="text-[var(--anl-text-muted)]">{message}</div>
  </motion.div>
);

interface ErrorStateProps {
  error: string | null;
  clearFilters: () => void;
}

export const ErrorState = ({ error, clearFilters }: ErrorStateProps) => (
  <motion.div
    className="p-6 text-center"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.2 }}
  >
    <div className="mb-2 text-[var(--anl-error)]">Error loading data</div>
    <div className="text-sm text-[var(--anl-text-muted)]">{error}</div>
    <button
      onClick={() => clearFilters()}
      className="bg-primary hover:bg-primary/90 mt-4 rounded px-4 py-2 text-white transition-colors"
    >
      Clear Filters & Try Again
    </button>
  </motion.div>
);

interface TableHeaderProps {
  title: string;
  subtitle?: string;
  hasActiveFilters?: boolean;
  clearFilters?: () => void;
  searchQuery?: string;
  setSearchQuery?: (query: string) => void;
  setCurrentPage?: (page: number) => void;
}

export const TableHeader = ({
  title,
  subtitle,
  hasActiveFilters,
  clearFilters,
  searchQuery = '',
  setSearchQuery,
  setCurrentPage,
}: TableHeaderProps) => (
  <div
    className="flex justify-between px-3 py-2.5 text-white"
    style={{ backgroundColor: '#253870' }}
  >
    <div className="flex flex-1 items-center">
      {setSearchQuery && (
        <div className="relative flex w-full items-center rounded-md bg-white/10 px-3 py-1.5">
          <Search size={15} className="mr-2 shrink-0 text-white/60" aria-hidden="true" />
          <input
            type="text"
            placeholder="Search users…"
            aria-label="Search users"
            className="w-full border-none bg-transparent text-sm text-white placeholder-white/50 outline-none focus-visible:outline-none"
            value={searchQuery}
            onChange={e => {
              setSearchQuery(e.target.value);
              setCurrentPage && setCurrentPage(1);
            }}
          />
          <AnimatePresence>
            {searchQuery && (
              <motion.button
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
                aria-label="Clear search"
                className="text-white/60 hover:text-white focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white/50"
                onClick={() => setSearchQuery('')}
              >
                <X size={14} aria-hidden="true" />
              </motion.button>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
    <div className="flex items-center gap-2 pl-3">
      {hasActiveFilters && clearFilters && (
        <button
          onClick={() => clearFilters()}
          aria-label="Clear all filters"
          className="flex items-center gap-1 rounded px-2 py-1 text-sm text-white/70 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white/50"
        >
          <X size={16} aria-hidden="true" />
          <span className="hidden sm:inline">Clear</span>
        </button>
      )}
      <div className="rounded py-1 pr-2 text-sm font-semibold text-white">
        {title}
        {subtitle && (
          <div className="mt-0.5 flex items-center text-xs text-white/70">
            <span>{subtitle}</span>
            {hasActiveFilters && clearFilters && (
              <button
                onClick={() => clearFilters()}
                aria-label="Clear filter"
                className="ml-2 rounded p-1 text-white/60 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white/50"
              >
                <X size={12} aria-hidden="true" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  </div>
);

interface UserRowProps {
  admin: AdminData;
  getUserName: (admin: AdminData) => string;
  icon: React.ReactNode;
  action?: React.ReactNode;
}

export const UserRow = ({ admin, getUserName, icon, action }: UserRowProps) => (
  <motion.div
    className="flex items-center justify-between px-6 py-3 hover:bg-[var(--anl-surface-elev)]"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.2 }}
  >
    <div className="flex items-center truncate text-sm font-medium text-[var(--anl-text)]">
      {icon}
      {getUserName(admin)}
    </div>
    {action}
  </motion.div>
);

interface TableSectionProps {
  title: string;
  children: React.ReactNode;
}

export const TableSection = ({ title, children }: TableSectionProps) => {
  const isMobile = useIsMobile();
  return (
    <div className="overflow-hidden">
      {title && (
        <div className="border-b border-[var(--anl-border)]">
          <div className="bg-[var(--anl-surface-elev)] px-6 py-3 text-sm font-medium text-[var(--anl-text-muted)]">
            {title}
          </div>
        </div>
      )}
      <div className={`overflow-y-auto ${isMobile ? 'max-h-[55vh] min-h-[320px]' : 'max-h-[480px]'}`}>
        <AnimatePresence mode="wait">{children}</AnimatePresence>
      </div>
    </div>
  );
};

interface TablePaginationProps {
  totalPages: number;
  currentPage: number;
  onPageChange: (page: number) => void;
}

export const TablePagination = ({
  totalPages,
  currentPage,
  onPageChange,
}: TablePaginationProps) => (
  <div>
    <Pagination
      totalPages={totalPages}
      currentPage={currentPage}
      onPageChange={onPageChange}
      variant="dark"
    />
  </div>
);

interface TableContainerProps {
  children: React.ReactNode;
  className?: string;
}

export const TableContainer = ({ children, className = 'w-full' }: TableContainerProps) => (
  <motion.div
    className={className}
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.2 }}
  >
    <div className="overflow-hidden rounded-lg bg-[var(--anl-surface)] shadow-md">{children}</div>
  </motion.div>
);
